import React, { useState, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import Layout from '../components/Layout';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import axios from 'axios';
import {
  Search, Upload, Loader2, FileType, X, Car, Cpu,
  CheckCircle, XCircle, AlertTriangle, Download, HardDrive,
  Clock, RotateCcw, Shield, ChevronDown, ChevronUp, Brain,
  Target, ArrowRight, CircleCheck,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CLASSIFICATION_CONFIG = {
  ORI: { label: 'ARCHIVO ORIGINAL', color: 'text-green-400', bg: 'bg-green-500/10 border-green-500/30', icon: CheckCircle },
  MOD: { label: 'ARCHIVO MODIFICADO', color: 'text-yellow-400', bg: 'bg-yellow-500/10 border-yellow-500/30', icon: AlertTriangle },
  NO_ENCONTRADO: { label: 'NO ENCONTRADO', color: 'text-red-400', bg: 'bg-red-500/10 border-red-500/30', icon: XCircle },
  NO_CONCLUYENTE: { label: 'NO CONCLUYENTE', color: 'text-zinc-400', bg: 'bg-zinc-500/10 border-zinc-500/30', icon: AlertTriangle },
};

const ScoreBadge = ({ score }) => {
  const color = score >= 80 ? 'text-green-400 bg-green-500/10 border-green-500/30'
    : score >= 50 ? 'text-yellow-400 bg-yellow-500/10 border-yellow-500/30'
    : score >= 20 ? 'text-orange-400 bg-orange-500/10 border-orange-500/30'
    : 'text-zinc-400 bg-zinc-500/10 border-zinc-500/30';
  return (
    <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-sm font-mono font-bold border ${color}`} data-testid="score-badge">
      {score}
    </span>
  );
};

const CandidateCard = ({ candidate, isSelected, onSelect, selecting, index }) => {
  const selected = isSelected;
  return (
    <div
      className={`border rounded-lg p-4 transition-all duration-200 ${
        selected
          ? 'border-green-500/50 bg-green-500/5 ring-1 ring-green-500/30'
          : 'border-white/10 bg-surface hover:border-white/20'
      }`}
      data-testid={`candidate-card-${index}`}
    >
      <div className="flex items-start gap-4">
        {/* Score */}
        <div className="flex-shrink-0 pt-0.5">
          <ScoreBadge score={candidate.score} />
        </div>

        {/* Info */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <Car className="w-4 h-4 text-zinc-400 flex-shrink-0" />
            <p className="text-white font-medium truncate">
              {candidate.vehicle || 'N/A'} {candidate.vehicle_year}
            </p>
          </div>
          <p className="text-zinc-400 text-xs font-mono mb-2">
            ECU: {candidate.ecu_type || candidate.ecu_family || '—'}
          </p>

          {/* Metadata row */}
          <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs">
            {candidate.hw_id && (
              <span className="text-zinc-500">HW: <span className="text-zinc-300 font-mono">{candidate.hw_id}</span></span>
            )}
            {candidate.sw_id && (
              <span className="text-zinc-500">SW: <span className="text-zinc-300 font-mono">{candidate.sw_id}</span></span>
            )}
            {candidate.bosch_cal_variant && (
              <span className="text-zinc-500">CAL: <span className="text-amber-400 font-mono font-bold">{candidate.bosch_cal_variant}</span></span>
            )}
            {candidate.cal_id && !candidate.bosch_cal_variant && (
              <span className="text-zinc-500">CAL: <span className="text-zinc-300 font-mono">{candidate.cal_id}</span></span>
            )}
          </div>

          {/* Matched fields */}
          <div className="flex flex-wrap gap-1 mt-2">
            {candidate.matched_fields?.map((f) => (
              <span key={f} className="text-[10px] px-1.5 py-0.5 rounded bg-primary/10 text-primary font-mono">
                {f}
              </span>
            ))}
          </div>
        </div>

        {/* Action */}
        <div className="flex-shrink-0">
          {selected ? (
            <div className="flex items-center gap-2 text-green-400" data-testid={`selected-indicator-${index}`}>
              <CircleCheck className="w-6 h-6" />
              <span className="text-xs font-heading uppercase tracking-wider hidden sm:inline">Seleccionado</span>
            </div>
          ) : (
            <Button
              size="sm"
              onClick={() => onSelect(candidate.solution_id)}
              disabled={selecting === candidate.solution_id}
              className="bg-primary hover:bg-primary/90 text-white text-xs"
              data-testid={`select-ori-${index}`}
            >
              {selecting === candidate.solution_id ? (
                <Loader2 className="w-3 h-3 animate-spin" />
              ) : (
                <>Seleccionar<ArrowRight className="w-3 h-3 ml-1" /></>
              )}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

const OriSearchPage = () => {
  const { token, user, refreshUser } = useAuth();
  const [file, setFile] = useState(null);
  const [searching, setSearching] = useState(false);
  const [result, setResult] = useState(null);
  const [dragOver, setDragOver] = useState(false);
  const [restoring, setRestoring] = useState(false);
  const [history, setHistory] = useState([]);
  const [showHistory, setShowHistory] = useState(false);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [expandedDiff, setExpandedDiff] = useState(false);
  // Identify & Match state
  const [identifying, setIdentifying] = useState(false);
  const [matchResult, setMatchResult] = useState(null);
  const [selectingOri, setSelectingOri] = useState(null);
  const [selectedOriId, setSelectedOriId] = useState(null);
  const [matchHistory, setMatchHistory] = useState([]);
  // ECU confirmation step
  const [ecuConfirmStep, setEcuConfirmStep] = useState(null); // null | {ecu, candidates_all}
  const [filteredCandidates, setFilteredCandidates] = useState(null);

  const headers = { Authorization: `Bearer ${token}` };

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    setDragOver(false);
    const dropped = e.dataTransfer.files[0];
    if (dropped) { setFile(dropped); setResult(null); setMatchResult(null); setSelectedOriId(null); setEcuConfirmStep(null); setFilteredCandidates(null); }
  }, []);

  const handleFileSelect = (e) => {
    const selected = e.target.files[0];
    if (selected) { setFile(selected); setResult(null); setMatchResult(null); setSelectedOriId(null); setEcuConfirmStep(null); setFilteredCandidates(null); }
  };

  const handleSearch = async () => {
    if (!file) return toast.error('Selecciona un archivo');
    setSearching(true);
    setResult(null);
    setMatchResult(null);
    setSelectedOriId(null);
    setEcuConfirmStep(null);
    setFilteredCandidates(null);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await axios.post(`${API}/ori-search`, fd, {
        headers: { ...headers, 'Content-Type': 'multipart/form-data' },
      });
      setResult(res.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error en la búsqueda');
    } finally {
      setSearching(false);
    }
  };

  const processMatchResult = (data) => {
    const detectedEcu = data.mod_metadata?.ecu_family;
    const candidates = data.candidates || [];

    // If AI detected ECU and there are multiple candidates → ask confirmation
    if (detectedEcu && candidates.length > 1) {
      setEcuConfirmStep({ ecu: detectedEcu, candidates_all: candidates });
      setFilteredCandidates(null);
    } else {
      // No ECU detected or only 1 candidate → show directly
      setEcuConfirmStep(null);
      setFilteredCandidates(candidates.slice(0, 5));
    }
  };

  const handleEcuConfirm = (confirmed) => {
    if (!ecuConfirmStep) return;
    if (confirmed) {
      // Filter by matching ECU family
      const ecuNorm = ecuConfirmStep.ecu.toUpperCase().replace(/[\s.\-_/]/g, '');
      const filtered = ecuConfirmStep.candidates_all.filter(c => {
        const cEcu = (c.ecu_type || c.ecu_family || '').toUpperCase().replace(/[\s.\-_/]/g, '');
        return cEcu && (cEcu.includes(ecuNorm) || ecuNorm.includes(cEcu));
      });
      setFilteredCandidates((filtered.length > 0 ? filtered : ecuConfirmStep.candidates_all).slice(0, 5));
    } else {
      // Show all candidates (top 5)
      setFilteredCandidates(ecuConfirmStep.candidates_all.slice(0, 5));
    }
    setEcuConfirmStep(null);
  };

  const handleIdentifyAndMatch = async () => {
    if (!result?.id) return;
    setIdentifying(true);
    setMatchResult(null);
    setSelectedOriId(null);
    setEcuConfirmStep(null);
    setFilteredCandidates(null);
    try {
      const res = await axios.post(`${API}/restore-ori/identify-and-match/${result.id}`, {}, { headers });
      setMatchResult(res.data);
      processMatchResult(res.data);
      if (res.data.candidates?.length > 0) {
        toast.success(`${res.data.candidates.length} candidato(s) ORI encontrado(s)`);
      } else {
        toast.info('No se encontraron ORIs compatibles');
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error en identificación IA');
    } finally {
      setIdentifying(false);
    }
  };

  const handleDirectIdentify = async () => {
    if (!file) return toast.error('Selecciona un archivo');
    setIdentifying(true);
    setMatchResult(null);
    setResult(null);
    setSelectedOriId(null);
    setEcuConfirmStep(null);
    setFilteredCandidates(null);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await axios.post(`${API}/restore-ori/identify-and-match-upload`, fd, {
        headers: { ...headers, 'Content-Type': 'multipart/form-data' },
      });
      setMatchResult(res.data);
      setResult({
        id: res.data.search_id,
        classification: 'MOD',
        file_hash: '',
        file_size: file.size,
        upload_filename: file.name,
        match_type: 'ai_identify',
        match_solution_id: null,
        similarity_score: 0,
        diff_summary: null,
        solution_info: null,
      });
      processMatchResult(res.data);
      if (res.data.candidates?.length > 0) {
        toast.success(`${res.data.candidates.length} candidato(s) ORI encontrado(s)`);
      } else {
        toast.info('No se encontraron ORIs compatibles en la BD');
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error en identificación IA');
    } finally {
      setIdentifying(false);
    }
  };

  const handleSelectOri = async (solutionId) => {
    if (!result?.id) return;
    setSelectingOri(solutionId);
    try {
      const res = await axios.post(`${API}/restore-ori/select-ori`, { search_id: result.id, solution_id: solutionId }, { headers });
      toast.success('ORI seleccionado');
      setSelectedOriId(solutionId);
      setResult(prev => ({
        ...prev,
        match_solution_id: solutionId,
        match_type: 'ai_selected',
        solution_info: res.data.solution_info,
        diff_summary: res.data.diff_summary || prev.diff_summary,
        classification: res.data.classification || prev.classification,
      }));
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error al seleccionar ORI');
    } finally {
      setSelectingOri(null);
    }
  };

  const handleRestore = async () => {
    if (!result?.id) return;
    setRestoring(true);
    try {
      const res = await axios.post(`${API}/ori-search/${result.id}/restore`, {}, { headers });
      toast.success(`ORI restaurado. ${res.data.credits_charged > 0 ? `-${res.data.credits_charged} créditos` : ''}`);
      try {
        const dlRes = await axios.get(`${API}/downloads/${res.data.download_token}`, { responseType: 'blob' });
        const url = window.URL.createObjectURL(dlRes.data);
        const a = document.createElement('a'); a.href = url; a.download = res.data.filename || 'original.bin'; a.click();
        window.URL.revokeObjectURL(url);
      } catch { /* user can retry with button */ }
      setResult(prev => ({ ...prev, restored: true, download_token: res.data.download_token }));
      refreshUser();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error al restaurar');
    } finally {
      setRestoring(false);
    }
  };

  const fetchHistory = async () => {
    setLoadingHistory(true);
    try {
      const [searchRes, matchRes] = await Promise.all([
        axios.get(`${API}/ori-search/history`, { headers }),
        axios.get(`${API}/restore-ori/match-history`, { headers }),
      ]);
      setHistory(searchRes.data.searches || []);
      setMatchHistory(matchRes.data.runs || []);
    } catch { toast.error('Error al cargar historial'); }
    finally { setLoadingHistory(false); }
  };

  const toggleHistory = () => {
    if (!showHistory) fetchHistory();
    setShowHistory(!showHistory);
  };

  const reset = () => {
    setFile(null); setResult(null); setExpandedDiff(false);
    setMatchResult(null); setSelectingOri(null); setSelectedOriId(null);
    setEcuConfirmStep(null); setFilteredCandidates(null);
  };

  const classInfo = result ? CLASSIFICATION_CONFIG[result.classification] || CLASSIFICATION_CONFIG.NO_ENCONTRADO : null;
  const showIdentifyBtn = result && ['MOD', 'NO_ENCONTRADO', 'NO_CONCLUYENTE'].includes(result.classification);
  const displayCandidates = filteredCandidates;

  return (
    <Layout>
      <div className="max-w-4xl mx-auto" data-testid="ori-search-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
          <div>
            <h1 className="font-heading text-3xl sm:text-4xl font-bold text-white">BUSCAR ORI</h1>
            <p className="text-zinc-400 mt-1">Sube un archivo para verificar si es ORI o MOD y restaurar al original</p>
          </div>
          <Button variant="outline" onClick={toggleHistory} className="border-white/10 text-white" data-testid="toggle-history-btn">
            <Clock className="w-4 h-4 mr-2" />Historial
          </Button>
        </div>

        {/* Upload Zone */}
        {!result && (
          <div className="bg-surface border border-white/10 rounded-md p-8 mb-6" data-testid="upload-zone">
            <div
              className={`border-2 border-dashed rounded-lg p-12 text-center transition-colors cursor-pointer ${
                dragOver ? 'border-primary bg-primary/5' : file ? 'border-green-500/50 bg-green-500/5' : 'border-white/10 hover:border-white/20'
              }`}
              onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
              onDragLeave={() => setDragOver(false)}
              onDrop={handleDrop}
              onClick={() => document.getElementById('ori-file-input').click()}
            >
              <input type="file" id="ori-file-input" className="hidden" accept=".bin,.ori,.hex,.mmf,.slv,.map" onChange={handleFileSelect} />
              {file ? (
                <div className="space-y-3">
                  <FileType className="w-12 h-12 text-green-400 mx-auto" />
                  <p className="text-white font-medium">{file.name}</p>
                  <p className="text-zinc-500 text-sm">{(file.size / 1024).toFixed(1)} KB</p>
                  <button onClick={(e) => { e.stopPropagation(); reset(); }} className="text-zinc-500 hover:text-red-400 text-xs">
                    <X className="w-3 h-3 inline mr-1" />Cambiar archivo
                  </button>
                </div>
              ) : (
                <div className="space-y-3">
                  <Upload className="w-12 h-12 text-zinc-500 mx-auto" />
                  <p className="text-white font-medium">Arrastra tu archivo aquí</p>
                  <p className="text-zinc-500 text-sm">o haz clic para seleccionar</p>
                  <p className="text-zinc-600 text-xs">.bin, .ori, .hex, .mmf, .slv, .map (máx 20MB)</p>
                </div>
              )}
            </div>

            <div className="flex flex-col sm:flex-row gap-3 mt-6">
              <Button onClick={handleSearch} disabled={!file || searching || identifying}
                className="flex-1 bg-primary hover:bg-primary/90 font-heading uppercase tracking-wider h-12" data-testid="search-ori-btn">
                {searching ? <><Loader2 className="w-5 h-5 mr-2 animate-spin" /> Analizando...</> : <><Search className="w-5 h-5 mr-2" /> Buscar ORI</>}
              </Button>
              <Button onClick={handleDirectIdentify} disabled={!file || searching || identifying} variant="outline"
                className="flex-1 border-blue-500/30 text-blue-400 hover:bg-blue-500/10 font-heading uppercase tracking-wider h-12" data-testid="identify-match-btn">
                {identifying ? <><Loader2 className="w-5 h-5 mr-2 animate-spin" /> Identificando con IA...</> : <><Brain className="w-5 h-5 mr-2" /> Identificar y buscar ORI (IA)</>}
              </Button>
            </div>
          </div>
        )}

        {/* Results */}
        {result && (
          <div className="space-y-4" data-testid="search-results">
            {/* Classification Badge */}
            <div className={`border rounded-md p-6 ${classInfo.bg}`} data-testid={`classification-${result.classification}`}>
              <div className="flex items-center gap-3 mb-2">
                <classInfo.icon className={`w-8 h-8 ${classInfo.color}`} />
                <div>
                  <h2 className={`font-heading text-2xl font-bold ${classInfo.color}`}>{classInfo.label}</h2>
                  <p className="text-zinc-400 text-sm">{result.diff_summary?.reason || ''}</p>
                </div>
              </div>
              {result.similarity_score > 0 && result.similarity_score < 1 && (
                <div className="mt-3 flex items-center gap-2">
                  <span className="text-zinc-500 text-sm">Similitud:</span>
                  <div className="flex-1 max-w-xs bg-zinc-800 rounded-full h-2">
                    <div className={`h-2 rounded-full ${result.similarity_score >= 0.95 ? 'bg-green-500' : result.similarity_score >= 0.85 ? 'bg-yellow-500' : 'bg-red-500'}`}
                      style={{ width: `${result.similarity_score * 100}%` }} />
                  </div>
                  <span className="text-white text-sm font-mono">{(result.similarity_score * 100).toFixed(1)}%</span>
                </div>
              )}
            </div>

            {/* File Info */}
            <div className="bg-surface border border-white/10 rounded-md p-5">
              <div className="flex items-center gap-2 mb-3">
                <HardDrive className="w-4 h-4 text-zinc-400" />
                <h3 className="font-heading text-sm text-zinc-400 uppercase tracking-wider">Archivo subido</h3>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 text-sm">
                <div><span className="text-zinc-500">Nombre</span><p className="text-white truncate">{result.upload_filename}</p></div>
                <div><span className="text-zinc-500">Tamaño</span><p className="text-white font-mono">{(result.file_size / 1024).toFixed(1)} KB</p></div>
                <div><span className="text-zinc-500">SHA-256</span><p className="text-white font-mono text-xs truncate">{result.file_hash?.substring(0, 16)}...</p></div>
              </div>
            </div>

            {/* Solution Info (if match found via standard or AI selection) */}
            {result.solution_info && (
              <div className={`bg-surface border rounded-md p-5 ${selectedOriId ? 'border-green-500/30' : 'border-white/10'}`}>
                <div className="flex items-center gap-2 mb-3">
                  {selectedOriId ? <CircleCheck className="w-4 h-4 text-green-400" /> : <Car className="w-4 h-4 text-primary" />}
                  <h3 className={`font-heading text-sm uppercase tracking-wider ${selectedOriId ? 'text-green-400' : 'text-primary'}`}>
                    {selectedOriId ? 'ORI SELECCIONADO' : 'ORI Encontrado'}
                  </h3>
                </div>
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div><span className="text-zinc-500">Vehículo</span><p className="text-white">{result.solution_info.vehicle}</p></div>
                  <div><span className="text-zinc-500">ECU</span><p className="text-white">{result.solution_info.ecu_type}</p></div>
                  {result.solution_info.engine_code && (
                    <div><span className="text-zinc-500">Motor</span><p className="text-white">{result.solution_info.engine_code}</p></div>
                  )}
                </div>
              </div>
            )}

            {/* AI Analysis from standard search */}
            {result.ai_analysis && !matchResult && (
              <div className="bg-surface border border-blue-500/20 rounded-md p-5" data-testid="ai-analysis-panel">
                <div className="flex items-center gap-2 mb-3">
                  <Brain className="w-4 h-4 text-blue-400" />
                  <h3 className="font-heading text-sm text-blue-400 uppercase tracking-wider">Análisis IA</h3>
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
                  {result.ai_analysis.hw_id && <div><span className="text-zinc-500">HW</span><p className="text-white font-mono text-xs">{result.ai_analysis.hw_id}</p></div>}
                  {result.ai_analysis.sw_id && <div><span className="text-zinc-500">SW</span><p className="text-white font-mono text-xs">{result.ai_analysis.sw_id}</p></div>}
                  {result.ai_analysis.ecu_family && <div><span className="text-zinc-500">ECU</span><p className="text-white text-xs">{result.ai_analysis.ecu_family}</p></div>}
                  {result.ai_analysis.oem && <div><span className="text-zinc-500">OEM</span><p className="text-white text-xs">{result.ai_analysis.oem}</p></div>}
                </div>
              </div>
            )}

            {/* Diff Summary */}
            {result.diff_summary && result.diff_summary.bytes_changed > 0 && (
              <div className="bg-surface border border-white/10 rounded-md p-5">
                <button onClick={() => setExpandedDiff(!expandedDiff)} className="w-full flex items-center justify-between" data-testid="toggle-diff-btn">
                  <div className="flex items-center gap-2">
                    <Shield className="w-4 h-4 text-yellow-500" />
                    <h3 className="font-heading text-sm text-yellow-500 uppercase tracking-wider">Diferencias</h3>
                  </div>
                  {expandedDiff ? <ChevronUp className="w-4 h-4 text-zinc-500" /> : <ChevronDown className="w-4 h-4 text-zinc-500" />}
                </button>
                <div className="grid grid-cols-3 gap-3 mt-3 text-sm">
                  <div><span className="text-zinc-500">Bytes</span><p className="text-white font-mono">{result.diff_summary.bytes_changed.toLocaleString()}</p></div>
                  <div><span className="text-zinc-500">%</span><p className="text-white font-mono">{result.diff_summary.pct_changed}%</p></div>
                  <div><span className="text-zinc-500">Regiones</span><p className="text-white font-mono">{result.diff_summary.total_regions}</p></div>
                </div>
                {expandedDiff && result.diff_summary.regions?.length > 0 && (
                  <div className="mt-4 pt-3 border-t border-white/10 space-y-1">
                    {result.diff_summary.regions.map((r, i) => (
                      <div key={i} className="flex items-center gap-3 text-xs font-mono bg-zinc-900/50 rounded px-3 py-1.5">
                        <span className="text-zinc-500">#{i + 1}</span>
                        <span className="text-white">{r.offset_start_hex} - {r.offset_end_hex}</span>
                        <span className="text-zinc-400">({r.size.toLocaleString()} bytes)</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* ===== IDENTIFY & MATCH BUTTON (before AI result) ===== */}
            {showIdentifyBtn && !matchResult && (
              <div className="bg-surface border border-blue-500/20 rounded-md p-6" data-testid="identify-match-section">
                <div className="flex items-start gap-4">
                  <Brain className="w-8 h-8 text-blue-400 flex-shrink-0 mt-0.5" />
                  <div className="flex-1">
                    <h3 className="font-heading text-lg font-bold text-blue-400">IDENTIFICAR Y BUSCAR ORI (IA)</h3>
                    <p className="text-zinc-400 text-sm mt-1">
                      Analiza los metadatos del firmware con IA y busca ORIs compatibles por HW/SW/ECU.
                    </p>
                    <Button onClick={handleIdentifyAndMatch} disabled={identifying}
                      className="mt-4 bg-blue-600 hover:bg-blue-500 text-white font-heading uppercase tracking-wider" data-testid="identify-match-trigger-btn">
                      {identifying ? <><Loader2 className="w-5 h-5 mr-2 animate-spin" /> Analizando con IA...</> : <><Brain className="w-5 h-5 mr-2" /> Identificar y buscar ORI (IA)</>}
                    </Button>
                  </div>
                </div>
              </div>
            )}

            {/* ===== ECU CONFIRMATION STEP ===== */}
            {ecuConfirmStep && (
              <div className="bg-amber-500/5 border border-amber-500/30 rounded-lg p-6" data-testid="ecu-confirm-step">
                <div className="flex items-start gap-4">
                  <Cpu className="w-8 h-8 text-amber-400 flex-shrink-0 mt-0.5" />
                  <div className="flex-1">
                    <h3 className="font-heading text-lg font-bold text-amber-400 mb-2">CONFIRMA TU ECU</h3>
                    <p className="text-zinc-300 mb-4">
                      La IA ha detectado la siguiente ECU en tu archivo. ¿Es correcta?
                    </p>
                    <div className="bg-surface rounded-lg p-4 mb-4 flex items-center gap-3">
                      <Cpu className="w-6 h-6 text-amber-400" />
                      <span className="text-white font-mono font-bold text-xl">{ecuConfirmStep.ecu}</span>
                    </div>
                    <div className="flex gap-3">
                      <Button onClick={() => handleEcuConfirm(true)}
                        className="bg-green-600 hover:bg-green-500 text-white px-8" data-testid="ecu-confirm-yes">
                        <CheckCircle className="w-4 h-4 mr-2" />Sí, es mi ECU
                      </Button>
                      <Button onClick={() => handleEcuConfirm(false)}
                        variant="outline" className="border-zinc-500/30 text-zinc-400 hover:bg-white/5 px-8" data-testid="ecu-confirm-no">
                        <XCircle className="w-4 h-4 mr-2" />No / No estoy seguro
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* ===== MATCH RESULTS: Metadata + Candidates ===== */}
            {matchResult && !ecuConfirmStep && (
              <div className="space-y-4" data-testid="match-result-section">
                {/* MOD Metadata */}
                {matchResult.mod_metadata && (() => {
                  const m = matchResult.mod_metadata;
                  const items = [
                    m.hw_id && { l: 'HW', v: m.hw_id },
                    m.sw_id && { l: 'SW', v: m.sw_id },
                    m.cal_id && { l: 'CAL', v: m.cal_id },
                    m.ecu_family && { l: 'ECU', v: m.ecu_family },
                  ].filter(Boolean);
                  return items.length > 0 ? (
                    <div className="bg-blue-500/5 border border-blue-500/20 rounded-md p-4" data-testid="mod-metadata-panel">
                      <div className="flex items-center gap-2 mb-3">
                        <Brain className="w-4 h-4 text-blue-400" />
                        <h3 className="font-heading text-sm text-blue-400 uppercase tracking-wider">Metadatos extraídos del MOD</h3>
                        {m.ai_confidence > 0 && <span className="text-xs font-mono text-blue-400/60 ml-auto">IA: {Math.round(m.ai_confidence * 100)}%</span>}
                      </div>
                      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
                        {items.map(({ l, v }) => <div key={l}><span className="text-zinc-500 text-xs">{l}</span><p className="text-white font-mono text-xs">{v}</p></div>)}
                      </div>
                    </div>
                  ) : null;
                })()}

                {/* Candidates list */}
                {displayCandidates && displayCandidates.length > 0 ? (
                  <div data-testid="candidates-list">
                    <div className="flex items-center gap-2 mb-3">
                      <Target className="w-4 h-4 text-primary" />
                      <h3 className="font-heading text-sm text-primary uppercase tracking-wider">
                        Candidatos ORI ({displayCandidates.length})
                      </h3>
                    </div>
                    <div className="space-y-3">
                      {displayCandidates.map((c, i) => (
                        <CandidateCard
                          key={c.solution_id}
                          candidate={c}
                          index={i}
                          isSelected={selectedOriId === c.solution_id}
                          onSelect={handleSelectOri}
                          selecting={selectingOri}
                        />
                      ))}
                    </div>
                  </div>
                ) : displayCandidates && displayCandidates.length === 0 ? (
                  <div className="bg-surface border border-white/10 rounded-md p-6 text-center" data-testid="no-candidates">
                    <XCircle className="w-10 h-10 text-red-400 mx-auto mb-3" />
                    <h3 className="text-white font-heading font-bold mb-2">NO SE ENCONTRÓ ORI COMPATIBLE</h3>
                    <p className="text-zinc-400 text-sm mb-2">No hay archivos originales en la BD con el mismo tamaño y metadatos compatibles.</p>
                    {matchResult.mod_metadata && (
                      <div className="mt-4 bg-zinc-900/50 rounded-md p-3 inline-block text-left">
                        <p className="text-zinc-500 text-xs mb-1">IDs detectados para búsqueda manual:</p>
                        {matchResult.mod_metadata.hw_id && <p className="text-zinc-300 text-xs font-mono">HW: {matchResult.mod_metadata.hw_id}</p>}
                        {matchResult.mod_metadata.sw_id && <p className="text-zinc-300 text-xs font-mono">SW: {matchResult.mod_metadata.sw_id}</p>}
                        {matchResult.mod_metadata.cal_id && <p className="text-zinc-300 text-xs font-mono">CAL: {matchResult.mod_metadata.cal_id}</p>}
                        {matchResult.mod_metadata.ecu_family && <p className="text-zinc-300 text-xs font-mono">ECU: {matchResult.mod_metadata.ecu_family}</p>}
                      </div>
                    )}
                  </div>
                ) : null}
              </div>
            )}

            {/* Restore Button */}
            {(result.classification === 'MOD' || result.classification === 'ORI' || result.classification === 'NO_CONCLUYENTE') && result.match_solution_id && !result.restored && (
              <div className={`bg-surface border rounded-md p-5 ${selectedOriId ? 'border-green-500/30 bg-green-500/5' : 'border-primary/20'}`} data-testid="restore-section">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-white font-heading font-bold">
                      {result.classification === 'ORI' ? 'DESCARGAR ARCHIVO ORIGINAL' : 'RESTAURAR ARCHIVO ORIGINAL'}
                    </h3>
                    <p className="text-zinc-400 text-sm mt-1">
                      {result.classification === 'ORI' ? 'Descarga una copia del archivo ORI verificado' : 'Descarga el archivo ORI correspondiente a este MOD'}
                    </p>
                    {result.diff_summary && result.diff_summary.bytes_changed > 0 && selectedOriId && (
                      <p className="text-yellow-400/80 text-xs mt-2 font-mono">
                        Diff: {result.diff_summary.bytes_changed.toLocaleString()} bytes ({result.diff_summary.pct_changed}%) en {result.diff_summary.total_regions} región(es)
                      </p>
                    )}
                  </div>
                  {!user?.is_admin ? (
                    <div className="text-right">
                      <p className="text-primary font-mono font-bold text-lg">20 créditos</p>
                      <p className="text-zinc-500 text-xs">Saldo: {user?.credits || 0}</p>
                    </div>
                  ) : (
                    <div className="text-right"><p className="text-green-400 font-mono font-bold text-sm">ADMIN - Gratis</p></div>
                  )}
                </div>
                <Button onClick={handleRestore}
                  disabled={restoring || (!user?.is_admin && (user?.credits || 0) < 20)}
                  className="w-full mt-4 bg-primary hover:bg-primary/90 font-heading uppercase tracking-wider h-12" data-testid="restore-ori-btn">
                  {restoring ? <><Loader2 className="w-5 h-5 mr-2 animate-spin" /> Procesando...</>
                    : (!user?.is_admin && (user?.credits || 0) < 20) ? <><XCircle className="w-5 h-5 mr-2" /> Créditos insuficientes</>
                    : <><RotateCcw className="w-5 h-5 mr-2" /> {result.classification === 'ORI' ? 'Descargar ORI' : 'Restore ORI'} {user?.is_admin ? '' : '(20 créditos)'}</>}
                </Button>
              </div>
            )}

            {/* Already Restored */}
            {result.restored && result.download_token && (
              <div className="bg-green-500/10 border border-green-500/30 rounded-md p-5 text-center">
                <CheckCircle className="w-8 h-8 text-green-400 mx-auto mb-2" />
                <p className="text-green-400 font-heading font-bold mb-3">ORI RESTAURADO</p>
                <Button onClick={async () => {
                  try {
                    const dlRes = await axios.get(`${API}/downloads/${result.download_token}`, { responseType: 'blob' });
                    const url = window.URL.createObjectURL(dlRes.data); const a = document.createElement('a'); a.href = url; a.download = 'original.bin'; a.click(); window.URL.revokeObjectURL(url);
                  } catch { toast.error('Error al descargar'); }
                }}
                  className="bg-green-600 hover:bg-green-700" data-testid="download-restored-btn">
                  <Download className="w-4 h-4 mr-2" /> Descargar ORI
                </Button>
              </div>
            )}

            {/* No match */}
            {result.classification === 'NO_ENCONTRADO' && !matchResult && (
              <div className="bg-surface border border-white/10 rounded-md p-5 text-center">
                <XCircle className="w-10 h-10 text-red-400 mx-auto mb-3" />
                <h3 className="text-white font-heading font-bold mb-2">NO SE ENCONTRÓ ORI</h3>
                <p className="text-zinc-400 text-sm mb-4">No hay ningún archivo original que coincida con tu archivo.</p>
                <p className="text-zinc-500 text-sm">Usa el botón "Identificar y buscar ORI (IA)" arriba para una búsqueda avanzada.</p>
              </div>
            )}

            {/* New Search */}
            <Button variant="outline" onClick={reset} className="w-full border-white/10 text-white" data-testid="new-search-btn">
              <Search className="w-4 h-4 mr-2" /> Nueva búsqueda
            </Button>
          </div>
        )}

        {/* History */}
        {showHistory && (
          <div className="mt-8 space-y-4">
            <div className="bg-surface border border-white/10 rounded-md" data-testid="search-history">
              <div className="p-4 border-b border-white/10 flex items-center justify-between">
                <h3 className="font-heading text-white font-bold text-sm">HISTORIAL</h3>
                {loadingHistory && <Loader2 className="w-4 h-4 text-primary animate-spin" />}
              </div>
              <div className="divide-y divide-white/5">
                {history.length === 0 ? (
                  <p className="text-zinc-500 text-sm text-center py-8">Sin búsquedas anteriores</p>
                ) : history.map((s) => {
                  const hClass = CLASSIFICATION_CONFIG[s.classification] || CLASSIFICATION_CONFIG.NO_ENCONTRADO;
                  return (
                    <div key={s.id} className="p-4 flex items-center gap-4">
                      <hClass.icon className={`w-5 h-5 flex-shrink-0 ${hClass.color}`} />
                      <div className="flex-1 min-w-0">
                        <p className="text-white text-sm truncate">{s.upload_filename}</p>
                        <p className="text-zinc-500 text-xs">
                          {new Date(s.created_at).toLocaleString('es-ES')} -
                          <span className={`ml-1 ${hClass.color}`}>{hClass.label}</span>
                        </p>
                      </div>
                      {s.restored && s.download_token && (
                        <Button size="sm" variant="ghost" onClick={async () => {
                          try { const r = await axios.get(`${API}/downloads/${s.download_token}`, { responseType: 'blob' }); const u = window.URL.createObjectURL(r.data); const a = document.createElement('a'); a.href = u; a.download = s.upload_filename || 'original.bin'; a.click(); window.URL.revokeObjectURL(u); } catch {}
                        }} className="text-green-400">
                          <Download className="w-3 h-3" />
                        </Button>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>

            {matchHistory.length > 0 && (
              <div className="bg-surface border border-blue-500/20 rounded-md" data-testid="match-history">
                <div className="p-4 border-b border-white/10 flex items-center gap-2">
                  <Brain className="w-4 h-4 text-blue-400" />
                  <h3 className="font-heading text-blue-400 font-bold text-sm">IDENTIFICACIONES IA</h3>
                </div>
                <div className="divide-y divide-white/5">
                  {matchHistory.map((run) => (
                    <div key={run.id} className="p-4 flex items-center gap-4">
                      <Brain className="w-5 h-5 text-blue-400 flex-shrink-0" />
                      <div className="flex-1 min-w-0">
                        <p className="text-white text-sm truncate">{run.mod_filename}</p>
                        <p className="text-zinc-500 text-xs">
                          {new Date(run.created_at).toLocaleString('es-ES')} -
                          <span className="text-blue-400 ml-1">{run.total_candidates} candidato(s)</span>
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </Layout>
  );
};

export default OriSearchPage;
